import React, { useState } from 'react';
import { Download, ListPlus, Plus, Save, Send, X } from 'lucide-react';
import { Modal } from '../../common/Modal';
import Button from '../../common/Button';
import { lineTotal } from '../../../lib/quantity';
import ShareLinkPanel from '../parts/ShareLinkPanel';
import ApprovalsPanel from '../parts/ApprovalsPanel';
import ConvertToInvoiceButton from '../parts/ConvertToInvoiceButton';
import DepositControl from '../parts/DepositControl';
import PaymentsPanel from '../parts/PaymentsPanel';
import CannedJobPicker from '../parts/CannedJobPicker';

// Extracted verbatim from JobManagement's JSX (Task 8), converted to the
// shared Modal primitive (Plan 2b).
//
// isOpen is hardcoded true: the orchestrator's `showInvoiceModal &&` guard
// at the call site is what controls mounting. The `<h2>` rendering
// userPreferences.business_name below is invoice content, not chrome, and
// the `<X>` in the parts table is a remove-part button, not the close
// button — both stay.
const inputClass =
  'w-full border border-border-strong rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent';

// One form for both stages. A document is an estimate until the mechanic
// converts it; a document with no type yet (never saved) is a new estimate.
const InvoiceModal = ({
  setShowInvoiceModal,
  selectedJobForActions,
  invoiceData,
  editableInvoice,
  setEditableInvoice,
  editableParts,
  calculatedTotals,
  sendingInvoice,
  addPart,
  applyCannedJob,
  updatePart,
  removePart,
  sendInvoiceToCustomer,
  downloadInvoicePDF,
  saveInvoiceToDatabase,
  userPreferences,
  recordApproval,
  setShareLink,
  refreshInvoiceStatus,
  convertToInvoice
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const isEstimate = (invoiceData.document_type || 'estimate') === 'estimate';
  const setField = (field) => (e) => setEditableInvoice(prev => ({ ...prev, [field]: e.target.value }));
  return (
        <Modal
          isOpen
          onClose={() => setShowInvoiceModal(false)}
          title={isEstimate ? 'Estimate' : 'Invoice'}
          size="xl"
          // Escape is new with the Modal conversion and discards the whole
          // invoice — labor hours, rates, discount, notes and every added
          // part — with no prompt, and reopening re-fetches and overwrites
          // editableInvoice/editableParts, so the work is unrecoverable.
          // Backdrop dismissal already existed here and is left alone.
          closeOnEscape={false}
        >
          <h2 className="text-page-title font-display text-ink text-center mb-4">
            {isEstimate ? 'ESTIMATE' : 'INVOICE'}
          </h2>

          {/* Company Header */}
          {(userPreferences.business_name || userPreferences.business_phone || userPreferences.business_address) && (
            <div className="bg-ink text-white rounded-lg p-4 mb-6">
              <div className="text-center">
                {userPreferences.business_name && (
                  <h2 className="text-section-title font-bold">{userPreferences.business_name}</h2>
                )}
                {userPreferences.business_address && (
                  <p className="text-white/80 text-sm mt-1">{userPreferences.business_address}</p>
                )}
                {userPreferences.business_phone && (
                  <p className="text-white/80 text-sm">{userPreferences.business_phone}</p>
                )}
              </div>
            </div>
          )}

          {/* Invoice Status — an estimate has no payment status yet */}
          <div className="flex items-center justify-between mb-6">
            {!isEstimate && (
            <div className="flex items-center space-x-4">
              <label htmlFor="invoice-status" className="text-sm font-medium text-ink">Invoice Status:</label>
              <select
                id="invoice-status"
                value={editableInvoice.status}
                onChange={(e) => setEditableInvoice(prev => ({ ...prev, status: e.target.value }))}
                className="border border-border-strong rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            )}
            <div className="text-sm text-ink-muted">
              Date: {new Date(invoiceData.converted_at || invoiceData.created_at || Date.now()).toLocaleDateString()}
            </div>
          </div>

          {/* Job Information */}
          <div className="bg-surface-page rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-ink mb-2">Job Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-ink-muted">Job Number:</span>
                <div className="font-medium">{invoiceData.job_number}</div>
              </div>
              <div>
                <span className="text-ink-muted">Customer:</span>
                <div className="font-medium">{invoiceData.customer_name}</div>
                {selectedJobForActions?.customer_email && (
                  <div className="text-small text-ink-muted">{selectedJobForActions.customer_email}</div>
                )}
              </div>
              <div>
                <span className="text-ink-muted">Vehicle:</span>
                <div className="font-medium">{invoiceData.vehicle_info}</div>
              </div>
              <div>
                <span className="text-ink-muted">Diagnosis Date:</span>
                <div className="font-medium">
                  {invoiceData.diagnosis_date ? new Date(invoiceData.diagnosis_date).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
              <label className="block text-sm font-medium text-ink">
                <span className="block mb-1">VIN</span>
                <input aria-label="VIN" type="text" maxLength={17} value={editableInvoice.vin || ''} onChange={setField('vin')} className={inputClass} />
              </label>
              <label className="block text-sm font-medium text-ink">
                <span className="block mb-1">License plate</span>
                <input aria-label="License plate" type="text" maxLength={20} value={editableInvoice.license_plate || ''} onChange={setField('license_plate')} className={inputClass} />
              </label>
              <label className="block text-sm font-medium text-ink">
                <span className="block mb-1">Odometer in</span>
                <input aria-label="Odometer in" type="text" inputMode="numeric" value={editableInvoice.odometer_in ?? ''} onChange={setField('odometer_in')} className={inputClass} />
              </label>
            </div>
          </div>

          {/* Work description, and on an estimate the return-parts question */}
          <div className="mb-6 space-y-3">
            <label className="block text-sm font-medium text-ink">
              <span className="block mb-1">{isEstimate ? 'Work to be done' : 'Work performed'}</span>
              <textarea
                aria-label={isEstimate ? 'Work to be done' : 'Work performed'}
                rows={3}
                value={editableInvoice.work_description || ''}
                onChange={setField('work_description')}
                className={inputClass}
              />
            </label>
            {isEstimate && (
              <label className="block text-sm font-medium text-ink">
                <span className="block mb-1">Return replaced parts to customer?</span>
                <select
                  aria-label="Return replaced parts to customer?"
                  value={editableInvoice.return_parts || ''}
                  onChange={setField('return_parts')}
                  className={inputClass}
                >
                  <option value="">Not asked yet</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
            )}
          </div>

          {/* Editable Parts List */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-ink">Parts & Materials</h4>
              <div className="flex items-center gap-2">
                {isEstimate && (
                  <button
                    onClick={() => setPickerOpen(true)}
                    className="px-3 py-1 min-h-touch text-sm bg-surface-card text-ink border border-border-strong rounded-md hover:bg-surface-sunken flex items-center"
                  >
                    <ListPlus className="h-4 w-4 mr-1" />
                    Add canned job
                  </button>
                )}
                <button
                  onClick={addPart}
                  className="px-3 py-1 text-sm bg-success text-white rounded-md hover:bg-success/80 flex items-center"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Part
                </button>
              </div>
            </div>
            {/* A grid, not a table: a six-column table with fixed widths on four
                columns left the part name about three characters wide on a phone.
                Below sm each part stacks: name and line total, then part #, qty,
                unit cost and remove. */}
            <div className="border border-border-default rounded-lg overflow-hidden">
              <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_6rem_7rem_4.5rem_6.5rem_6rem_2.5rem] gap-2 px-3 py-2 bg-surface-page text-sm font-medium text-ink">
                <span>Part Name</span>
                <span>Part #</span>
                <span>Condition</span>
                <span className="text-center">Qty</span>
                <span className="text-right">Unit Cost</span>
                <span className="text-right">Total</span>
                <span aria-hidden="true"></span>
              </div>
              {editableParts.length === 0 ? (
                <p className="px-4 py-4 text-center text-ink-muted text-sm">
                  No parts added. Click "Add Part" to add parts to this invoice.
                </p>
              ) : (
                editableParts.map((part, index) => {
                  const n = index + 1;
                  return (
                    <div
                      key={part.id}
                      className="grid grid-cols-6 sm:grid-cols-[minmax(0,1fr)_6rem_7rem_4.5rem_6.5rem_6rem_2.5rem] gap-2 items-center px-3 py-2 border-t border-border-default"
                    >
                      <input
                        type="text"
                        aria-label={`Part ${n} name`}
                        value={part.part_name}
                        onChange={(e) => updatePart(part.id, 'part_name', e.target.value)}
                        placeholder="Part name"
                        className="col-span-4 sm:col-span-1 order-1 w-full border border-border-strong rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                      <span className="col-span-2 sm:col-span-1 order-2 sm:order-5 text-right text-sm font-medium">
                        ${lineTotal(part.quantity, part.unit_cost).toFixed(2)}
                      </span>
                      <input
                        type="text"
                        aria-label={`Part ${n} number`}
                        value={part.part_number}
                        onChange={(e) => updatePart(part.id, 'part_number', e.target.value)}
                        placeholder="Part #"
                        className="col-span-2 sm:col-span-1 order-3 sm:order-2 w-full border border-border-strong rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                      <select
                        aria-label={`Part ${n} condition`}
                        value={part.condition ?? 'new'}
                        onChange={(e) => updatePart(part.id, 'condition', e.target.value)}
                        className="col-span-2 sm:col-span-1 order-3 sm:order-3 w-full border border-border-strong rounded px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                      >
                        {part.condition === '' && <option value="">Not recorded</option>}
                        <option value="new">New</option>
                        <option value="used">Used</option>
                        <option value="rebuilt">Rebuilt</option>
                        <option value="reconditioned">Reconditioned</option>
                      </select>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0.01"
                        aria-label={`Part ${n} quantity`}
                        value={part.quantity}
                        onChange={(e) => updatePart(part.id, 'quantity', e.target.value)}
                        className="col-span-1 order-4 sm:order-3 text-center w-full border border-border-strong rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        aria-label={`Part ${n} unit cost`}
                        value={part.unit_cost}
                        onChange={(e) => updatePart(part.id, 'unit_cost', parseFloat(e.target.value) || 0)}
                        className="col-span-2 sm:col-span-1 order-5 sm:order-4 text-right w-full border border-border-strong rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                      <button
                        type="button"
                        onClick={() => removePart(part.id)}
                        aria-label={`Remove part ${n}`}
                        title="Remove part"
                        className="col-span-1 order-6 justify-self-center text-danger hover:text-danger/80 min-w-touch min-h-touch flex items-center justify-center"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Invoice Adjustments - Labor, Rate, Tax, Discount */}
          <div className="mb-6">
            <h4 className="font-semibold text-ink mb-3">Invoice Adjustments</h4>
            <div className="bg-surface-page rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Labor Hours</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editableInvoice.labor_hours}
                    onChange={(e) => setEditableInvoice(prev => ({
                      ...prev,
                      labor_hours: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Labor Rate ($/hr)</label>
                  <input
                    type="number"
                    min="0"
                    step="5"
                    value={editableInvoice.labor_rate}
                    onChange={(e) => setEditableInvoice(prev => ({
                      ...prev,
                      labor_rate: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">Tax Rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={editableInvoice.tax_rate}
                    onChange={(e) => setEditableInvoice(prev => ({
                      ...prev,
                      tax_rate: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1">
                    Discount
                    <select
                      value={editableInvoice.discount_type}
                      onChange={(e) => setEditableInvoice(prev => ({
                        ...prev,
                        discount_type: e.target.value
                      }))}
                      className="ml-2 text-small border rounded px-1 py-0.5 border-border-strong"
                    >
                      <option value="amount">$</option>
                      <option value="percentage">%</option>
                    </select>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step={editableInvoice.discount_type === 'percentage' ? '1' : '5'}
                    max={editableInvoice.discount_type === 'percentage' ? '100' : undefined}
                    value={editableInvoice.discount}
                    onChange={(e) => setEditableInvoice(prev => ({
                      ...prev,
                      discount: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Live Calculated Totals */}
          <div className="bg-accent/10 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-ink mb-3">Invoice Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-ink-muted">Parts & Materials:</span>
                <span className="font-medium">${calculatedTotals.parts_cost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">
                  Labor ({editableInvoice.labor_hours} hrs × ${editableInvoice.labor_rate}/hr):
                </span>
                <span className="font-medium">${calculatedTotals.labor_cost.toFixed(2)}</span>
              </div>
              {calculatedTotals.discount_amount > 0 && (
                <div className="flex justify-between text-success">
                  <span>
                    Discount ({editableInvoice.discount_type === 'percentage' 
                      ? `${editableInvoice.discount}%` 
                      : `$${editableInvoice.discount}`}):
                  </span>
                  <span className="font-medium">-${calculatedTotals.discount_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-accent/30 pt-2">
                <span className="text-ink-muted">Subtotal:</span>
                <span className="font-medium">${calculatedTotals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Tax ({editableInvoice.tax_rate.toFixed(1)}%):</span>
                <span className="font-medium">${calculatedTotals.tax_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-info border-t border-accent/30 pt-2">
                <span>Total:</span>
                <span>${calculatedTotals.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {isEstimate && (
            <DepositControl
              editableInvoice={editableInvoice}
              setEditableInvoice={setEditableInvoice}
              editableParts={editableParts}
              total={calculatedTotals.total}
            />
          )}

          <ApprovalsPanel
            authorizations={invoiceData.authorizations || []}
            total={calculatedTotals.total}
            recordApproval={recordApproval}
          />

          {/* Only a saved estimate has a row to link to. */}
          {isEstimate && invoiceData.invoice_number && (
            <ShareLinkPanel
              jobId={selectedJobForActions.id}
              shareLink={invoiceData.share_link || null}
              customerEmail={selectedJobForActions.customer_email}
              onChange={setShareLink}
            />
          )}

          {/* A saved document is what payments attach to. */}
          {invoiceData.invoice_number && (
            <PaymentsPanel
              jobId={selectedJobForActions.id}
              documentType={invoiceData.document_type || 'estimate'}
              onChange={refreshInvoiceStatus}
            />
          )}

          {/* Invoice Notes */}
          <div className="mb-6">
            <h4 className="font-semibold text-ink mb-2">Notes</h4>
            <textarea
              value={editableInvoice.notes}
              onChange={(e) => setEditableInvoice(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any notes or terms for this invoice..."
              rows={3}
              className="w-full border border-border-strong rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>

          <div className="-mx-6 -mb-4 mt-2 px-6 py-4 border-t border-border-default flex justify-between items-center">
            <Button variant="ghost" onClick={() => setShowInvoiceModal(false)}>
              Close
            </Button>

            {/* One solid action: saving is what commits the invoice. Send and
                Download were orange and accent fills beside a green Save --
                three colours competing for "do this". */}
            <div className="flex flex-wrap justify-end gap-3">
              {isEstimate && (
                <ConvertToInvoiceButton
                  authorizations={invoiceData.authorizations || []}
                  convertToInvoice={convertToInvoice}
                />
              )}
              {/* The customer email is worded as an invoice; estimates have
                  no email of their own yet. */}
              {!isEstimate && (
              <Button
                variant="secondary"
                icon={Send}
                loading={sendingInvoice}
                onClick={sendInvoiceToCustomer}
                disabled={!selectedJobForActions?.customer_email}
                title={!selectedJobForActions?.customer_email ? 'No customer email' : 'Send invoice to customer email'}
              >
                Send to Customer
              </Button>
              )}
              <Button variant="secondary" icon={Download} onClick={downloadInvoicePDF}>
                Download PDF
              </Button>
              <Button icon={Save} onClick={() => saveInvoiceToDatabase()}>
                {isEstimate ? 'Save Estimate' : 'Save Invoice'}
              </Button>
            </div>
          </div>
          {pickerOpen && (
            <CannedJobPicker onAdd={applyCannedJob} onClose={() => setPickerOpen(false)} />
          )}
        </Modal>
  );
};

export default InvoiceModal;
